import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import classnames from "helpers/classnames";
import ArrowImg from "images/components/dark-popover/teal-arrow.svg";

export default function DarkPopover({
  active,
  forcePosition,
  imageAlt,
  imageSrc,
  subcopy,
  title,
  visible,
  containerClass = ""
}) {
  const [isVisible, setIsVisible] = useState(visible);
  const [mirrored, setMirrored] = useState(forcePosition === "above");
  const popoverRef = useRef(null);

  const setVisibleIfActive = (v) => {
    if (active) {
      setIsVisible(v);
    }
  };

  const mirrorIfBelowFold = () => {
    // If forcePosition is set, use that instead of auto-calculation
    if (forcePosition === "below") {
      setMirrored(false);
      return;
    }
    if (forcePosition === "above") {
      setMirrored(true);
      return;
    }

    // Auto-calculate position based on viewport
    setMirrored(false);

    const popoverEl = popoverRef.current;
    if (popoverEl) {
      const contentBox = popoverEl
        .querySelector(".dark-popover")
        .getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const { scrollY } = window;

      if (contentBox.bottom + scrollY > windowHeight + scrollY) {
        setMirrored(true);
      }
    }
  };

  useEffect(() => {
    mirrorIfBelowFold();
    window.addEventListener("scroll", mirrorIfBelowFold);
    window.addEventListener("resize", mirrorIfBelowFold);
    return () => {
      window.removeEventListener("scroll", mirrorIfBelowFold);
      window.removeEventListener("resize", mirrorIfBelowFold);
    };
  }, [forcePosition]);

  return (
    <div
      className={classnames(
        containerClass,
        "popover-container",
        "dark-popover-container"
      )}
      role="presentation"
      onMouseEnter={() => setVisibleIfActive(true)}
      onMouseLeave={() => setVisibleIfActive(false)}
      onClick={() => setVisibleIfActive(!isVisible)}
      ref={popoverRef}
    >
      <i className="lc-icon lc-icon-information lc-icon-sm">
        <div
          className={classnames("dark-popover", {
            above: mirrored,
            below: !mirrored,
            visible: isVisible
          })}
        >
          <img
            alt="Arrow Icon"
            className={`arrow ${mirrored ? "bottom" : "top"} right`}
            src={ArrowImg}
          />
          <div className="content">
            <div className="text">
              <h1>{title}</h1>
              {subcopy.map((line) => {
                return (
                  <h2 key={line.text} className={line.alignment}>
                    {line.text}
                  </h2>
                );
              })}
            </div>
            <img
              className="image"
              src={imageSrc}
              alt={imageAlt}
              onLoad={mirrorIfBelowFold}
            />
          </div>
        </div>
      </i>
    </div>
  );
}

DarkPopover.propTypes = {
  active: PropTypes.bool,
  forcePosition: PropTypes.oneOf(["above", "below"]),
  imageAlt: PropTypes.string,
  imageSrc: PropTypes.string,
  subcopy: PropTypes.arrayOf(
    PropTypes.shape({
      alignment: PropTypes.string,
      text: PropTypes.string
    })
  ),
  title: PropTypes.string,
  visible: PropTypes.bool
};

DarkPopover.defaultProps = {
  active: false,
  forcePosition: undefined,
  imageAlt: "",
  imageSrc: "",
  subcopy: [],
  title: "",
  visible: false
};
